import React, { createContext, useContext, useState, useEffect } from "react";

const dict = {
  en: {
    appName: "GRC Task Manager", appTagline: "Cybersecurity & GRC Sandbox",
    search: "Search...", logout: "Log out", language: "العربية",
    dashboard: "Dashboard", useCases: "Use Cases", tasks: "Tasks", kanban: "Kanban Board",
    approvals: "Approvals", registers: "Registers", riskHeatMap: "Risk Heat Map",
    cyberGov: "Cybersecurity Governance", aiReadiness: "AI Readiness", aiProjects: "AI Project Management",
    regulatory: "Regulatory Compliance", businessImpact: "Business Impact", kri: "Key Risk Indicators",
    kpi: "Key Performance Indicators", businessValue: "Business Value", soc: "SOC Dashboard",
    appPerf: "Application Performance", netPerf: "Network Performance", soar: "SOAR Automation",
    auditLog: "Audit Log", userManager: "User Manager", integrations: "Integrations",
    add: "Add", edit: "Edit", delete: "Delete", save: "Save", cancel: "Cancel", actions: "Actions",
    readAloud: "Read aloud", play: "Play", pause: "Pause", speed: "Speed", transcript: "Transcript",
    total: "Total", open: "Open", critical: "Critical", overdue: "Overdue", dueSoon: "Due Soon",
    reminders: "Reminders", noItems: "No items found", status: "Status", owner: "Owner",
    priority: "Priority", severity: "Severity", allStatus: "All Status", generating: "Generating summary...",
    complianceScore: "Compliance Score", openRisks: "Open Risks", openIncidents: "Open Incidents",
    tasksByStage: "Tasks by Stage", useCasesByDomain: "Use Cases by Domain", sendReminders: "Send Reminders",
    filterHint: "Click a cell to filter risks", clearFilter: "Clear filter", role: "Role",
    welcome: "Welcome back", loginSub: "Log in to your account", continueGoogle: "Continue with Google",
    email: "Email", password: "Password", forgot: "Forgot password?", login: "Log in",
    noAccount: "Don't have an account?", createOne: "Create one", demoNote: "Demo mode — any credentials log you in",
  },
  ar: {
    appName: "مدير مهام الحوكمة", appTagline: "بيئة الأمن السيبراني والحوكمة",
    search: "بحث...", logout: "تسجيل الخروج", language: "English",
    dashboard: "لوحة المعلومات", useCases: "حالات الاستخدام", tasks: "المهام", kanban: "لوحة كانبان",
    approvals: "الموافقات", registers: "السجلات", riskHeatMap: "خريطة المخاطر",
    cyberGov: "حوكمة الأمن السيبراني", aiReadiness: "جاهزية الذكاء الاصطناعي", aiProjects: "إدارة مشاريع الذكاء الاصطناعي",
    regulatory: "الامتثال التنظيمي", businessImpact: "تأثير الأعمال", kri: "مؤشرات المخاطر الرئيسية",
    kpi: "مؤشرات الأداء الرئيسية", businessValue: "قيمة الأعمال", soc: "لوحة مركز العمليات",
    appPerf: "أداء التطبيقات", netPerf: "أداء الشبكة", soar: "أتمتة الاستجابة",
    auditLog: "سجل التدقيق", userManager: "إدارة المستخدمين", integrations: "التكاملات",
    add: "إضافة", edit: "تعديل", delete: "حذف", save: "حفظ", cancel: "إلغاء", actions: "إجراءات",
    readAloud: "قراءة صوتية", play: "تشغيل", pause: "إيقاف", speed: "السرعة", transcript: "النص",
    total: "الإجمالي", open: "مفتوح", critical: "حرج", overdue: "متأخر", dueSoon: "مستحق قريباً",
    reminders: "التذكيرات", noItems: "لا توجد عناصر", status: "الحالة", owner: "المالك",
    priority: "الأولوية", severity: "الخطورة", allStatus: "كل الحالات", generating: "جارٍ إنشاء الملخص...",
    complianceScore: "درجة الامتثال", openRisks: "المخاطر المفتوحة", openIncidents: "الحوادث المفتوحة",
    tasksByStage: "المهام حسب المرحلة", useCasesByDomain: "حالات الاستخدام حسب المجال", sendReminders: "إرسال التذكيرات",
    filterHint: "انقر على خلية لتصفية المخاطر", clearFilter: "مسح التصفية", role: "الدور",
    welcome: "مرحباً بعودتك", loginSub: "سجّل الدخول إلى حسابك", continueGoogle: "المتابعة عبر جوجل",
    email: "البريد الإلكتروني", password: "كلمة المرور", forgot: "نسيت كلمة المرور؟", login: "تسجيل الدخول",
    noAccount: "ليس لديك حساب؟", createOne: "أنشئ حساباً", demoNote: "وضع تجريبي — أي بيانات تسجّل دخولك",
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("grc_lang") || "en");
  const dir = lang === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    localStorage.setItem("grc_lang", lang);
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", lang);
  }, [lang, dir]);

  const t = (key) => (dict[lang] && dict[lang][key]) || (dict.en[key]) || key;
  const toggle = () => setLang((l) => (l === "en" ? "ar" : "en"));

  return <I18nContext.Provider value={{ lang, dir, t, setLang, toggle }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
